import { zodResolver } from '@hookform/resolvers/zod';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form/form';
import { Input } from '@/components/ui/input';
import { useAutoSave } from '@/hooks/use-auto-save';
import { nutrients } from '@/lib/consts';
import { IngredientNutrient, Nutrient } from '@/types';
import { calculateCaloriesPer100g } from '@/utils/ingredient-calculations';

import { useInsertIngredientNutrients } from '../api/ingredients-mutations';
import { IngredientWithDetails } from '../types/ingredient-types';
import { IngredientNutritionSearch } from './ingredient-nutrition-search';

// Generate Zod schema from field definitions
const createNutritionSchema = () => {
  const schemaObject = {} as Record<Nutrient, z.ZodNumber>;

  nutrients.forEach((field) => {
    const baseSchema = z
      .number()
      .min(0, `${field.label} must be a positive number`);
    schemaObject[field.key] = baseSchema;
  });

  return z.object(schemaObject);
};

const nutritionFormSchema = createNutritionSchema();
export type NutritionForm = z.infer<typeof nutritionFormSchema>;

// Generate default values from fields
const createDefaultValues = (ingredient: IngredientWithDetails) => {
  const defaults = {} as Record<Nutrient, number>;

  // Set all to 0 first
  nutrients.forEach((field) => {
    defaults[field.key] = 0; // Default to 0 for all nutrients
  });

  ingredient.latest_ingredient_nutrient.forEach((nutrient) => {
    defaults[nutrient.nutrient] = nutrient.value;
  });

  return defaults as NutritionForm;
};

interface IngredientNutritionTablePresentationProps {
  ingredient: IngredientWithDetails;
  onSubmit: (values: NutritionForm) => Promise<void>;
  onFoodSelect: (food: NutritionForm) => void;
  isEditable: boolean;
}

export function IngredientNutritionTablePresentation({
  ingredient,
  onSubmit,
  onFoodSelect,
  isEditable,
}: IngredientNutritionTablePresentationProps) {
  const form = useForm<NutritionForm>({
    resolver: zodResolver(nutritionFormSchema),
    defaultValues: createDefaultValues(ingredient),
  });

  useAutoSave(form, onSubmit);

  const calories = calculateCaloriesPer100g(
    form.watch() as Record<Nutrient, number>,
  );

  const handleFoodSelect = (food: NutritionForm) => {
    onFoodSelect(food);
    form.reset(food); // Update the form with new values
  };

  return (
    <div className="pt-6">
      <div className="flex w-full">
        <div className="flex-1">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-1">
              <div className="border border-gray-300 bg-white p-4 max-w-md">
                <div className="border-b-2 border-black pb-1 mb-2">
                  <h3 className="font-bold text-lg">Nutrition Facts</h3>
                  <p className="text-sm">per 100g</p>
                </div>
                <div className="border-0 p-0 space-y-0">
                  <div className="flex items-center justify-between py-1 border-b border-gray-200">
                    <div className="flex items-center flex-1">
                      <FormLabel className="text-sm font-semibold">
                        Calories
                      </FormLabel>
                    </div>
                    <div className="flex items-center gap-1 min-w-[80px] justify-end">
                      <span className="text-sm font-medium min-w-[15px]">
                        {/* TODO: Check rules for rounding */}
                        {Math.round(calories.kJ)} kJ /{' '}
                        {Math.round(calories.kcal)} kcal
                      </span>
                    </div>
                  </div>
                </div>
                {nutrients.map((field) => (
                  <FormField
                    key={field.key}
                    control={form.control}
                    name={field.key}
                    render={({ field: fieldProps }) => (
                      <FormItem className="border-0 p-0 space-y-0">
                        <div
                          className={`flex items-center justify-between py-1 ${
                            field.isSubField
                              ? 'pl-4 border-0'
                              : 'border-b border-gray-200'
                          }`}
                        >
                          <div className="flex items-center flex-1">
                            <FormLabel
                              className={`text-sm ${
                                field.isSubField
                                  ? 'font-normal text-gray-700'
                                  : 'font-semibold'
                              }`}
                            >
                              {field.label}
                            </FormLabel>
                          </div>

                          <div className="flex items-center gap-1 min-w-[80px] justify-end">
                            <FormControl>
                              <Input
                                type="number"
                                step="0.1"
                                min="0"
                                placeholder="0"
                                value={fieldProps.value}
                                onChange={(e) => {
                                  const value = e.target.value;
                                  fieldProps.onChange(
                                    value === '' ? 0 : parseFloat(value) || 0,
                                  );
                                }}
                                onFocus={(e) => e.target.select()}
                                disabled={!isEditable}
                                className="text-right border-0 shadow-none focus-visible:ring-0 w-12 px-1 h-6 text-sm bg-gray-50"
                              />
                            </FormControl>
                            <span className="text-sm font-medium min-w-[15px]">
                              {field.unit}
                            </span>
                          </div>
                        </div>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                ))}
              </div>
            </form>
          </Form>
        </div>
        <div className="flex-1 pl-6">
          {isEditable && (
            <>
              <p className="text-sm pb-4">
                For the most accurate info, ask your supplier, or check the USDA
                nutrition database below.
              </p>
              <IngredientNutritionSearch onFoodSelect={handleFoodSelect} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

interface IngredientNutritionProps {
  ingredient: IngredientWithDetails;
  isEditable: boolean;
}

export function IngredientNutrition({
  ingredient,
  isEditable,
}: IngredientNutritionProps) {
  const insertIngredientNutrients = useInsertIngredientNutrients();

  const handleSubmit = useCallback(
    async (values: NutritionForm) => {
      // Get the current state of nutrients
      const currentState = {} as Record<Nutrient, number>;
      ingredient.latest_ingredient_nutrient.forEach((nutrient) => {
        currentState[nutrient.nutrient] = nutrient.value;
      });

      // Compute the difference
      const diff: Omit<IngredientNutrient, 'id' | 'created_at'>[] = [];
      Object.keys(values).forEach((key) => {
        const nutrientKey = key as Nutrient;
        const newValue = values[nutrientKey];
        const currentValue = currentState[nutrientKey];

        // Only save if the value has changed
        if (newValue !== currentValue) {
          diff.push({
            nutrient: nutrientKey,
            value: newValue,
            organisation_id: ingredient.organisation_id,
            ingredient_id: ingredient.id,
          });
        }
      });

      // Save the differences to the server
      await insertIngredientNutrients.mutateAsync(diff);
    },
    [ingredient, insertIngredientNutrients],
  );

  const handleFoodSelect = useCallback(
    async (food: NutritionForm) => {
      await handleSubmit(food);
    },
    [handleSubmit],
  );

  return (
    <IngredientNutritionTablePresentation
      ingredient={ingredient}
      onSubmit={handleSubmit}
      onFoodSelect={handleFoodSelect}
      isEditable={isEditable}
    />
  );
}
